import { FC, useState, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MainContainer from '../components/ui_components/MainContainer';
import { apiResetPassword } from '../components/ui_components/ApiList';
import colors from '../styles/colors';

const PageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px 20px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: ${colors.bgColorBlack2};
  border: 2px solid ${colors.bgColorBordeaux};
  border-radius: 8px;
  padding: 32px;
`;

const CardTitle = styled.h1`
  color: white;
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 8px;
  text-align: center;
`;

const Divider = styled.hr`
  border: none;
  border-top: 2px solid ${colors.bgColorBordeaux};
  margin: 16px 0 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: white;
  font-size: 14px;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  background-color: #ffffff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 1;
  }
`;

const TogglePasswordBtn = styled.button`
  margin-top: 8px;
  padding: 4px 10px;
  font-size: 13px;
  border: 1px solid #6c757d;
  border-radius: 4px;
  background: transparent;
  color: #aaa;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #6c757d;
    color: #fff;
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: ${colors.bgColorBordeaux};
  color: #fff;
  transition: background 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background: darkgoldenrod;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin-top: 4px;
  margin-bottom: 0;
  color: #dc3545;
  font-size: 13px;
`;

const GeneralError = styled.div`
  background: rgba(220, 53, 69, 0.15);
  border: 1px solid #dc3545;
  border-radius: 6px;
  padding: 10px 14px;
  color: #dc3545;
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
`;

const SuccessBox = styled.div`
  text-align: center;
  color: #198754;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const HomeLink = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  font-size: 15px;
  font-weight: 600;
  border: 2px solid gold;
  border-radius: 6px;
  background: transparent;
  color: gold;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: gold;
    color: #000;
  }
`;

const InvalidLinkBox = styled.div`
  text-align: center;
  color: #999;
  font-size: 16px;
  padding: 20px 0;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #aaa;
  font-size: 14px;
  margin-top: 8px;
`;

const ResetPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [errorsForm, setErrorsForm] = useState<Record<string, string[]>>({});
  const [generalError, setGeneralError] = useState<string>('');

  const isLinkValid = !!token && !!email;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!token || !email) return;

    setIsLoading(true);
    setErrorsForm({});
    setGeneralError('');

    const formData = new FormData(event.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirm_password') as string;

    try {
      const response = await apiResetPassword({
        email,
        token,
        password,
        confirm_password: confirmPassword,
      });

      if (response.errors) {
        setErrorsForm(response.errors);
      } else if (response.message === 'Token non valido o scaduto') {
        setGeneralError(response.message);
      } else {
        setIsSuccess(true);
      }
    } catch {
      setGeneralError('Errore durante la richiesta. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainContainer>
      <PageWrapper>
        <Card>
          <CardTitle>Reimposta Password</CardTitle>
          <Divider />

          {!isLinkValid ? (
            <InvalidLinkBox>
              Il link non è valido. Assicurati di aver utilizzato il link ricevuto via email.
            </InvalidLinkBox>
          ) : isSuccess ? (
            <>
              <SuccessBox>Password aggiornata con successo!</SuccessBox>
              <HomeLink type="button" onClick={() => navigate('/')}>
                Torna alla Home
              </HomeLink>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              {generalError && <GeneralError>{generalError}</GeneralError>}

              <FormGroup>
                <FormLabel htmlFor="reset-password">Nuova Password:</FormLabel>
                <FormInput
                  type={showPassword ? 'text' : 'password'}
                  id="reset-password"
                  name="password"
                  disabled={isLoading}
                />
                {errorsForm.password && <ErrorText>{errorsForm.password[0]}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="reset-confirm-password">Conferma Password:</FormLabel>
                <FormInput
                  type={showPassword ? 'text' : 'password'}
                  id="reset-confirm-password"
                  name="confirm_password"
                  disabled={isLoading}
                />
                {errorsForm.confirm_password && (
                  <ErrorText>{errorsForm.confirm_password[0]}</ErrorText>
                )}
              </FormGroup>

              <TogglePasswordBtn type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? 'Nascondi' : 'Mostra Password'}
              </TogglePasswordBtn>

              <SubmitBtn type="submit" disabled={isLoading}>
                Reimposta Password
              </SubmitBtn>

              {isLoading && <LoadingText>Caricamento...</LoadingText>}
            </form>
          )}
        </Card>
      </PageWrapper>
    </MainContainer>
  );
};

export default ResetPassword;
